/**
 * calculations/orderVerify.js
 * ╔═══════════════════════════════════════════════════════════════════════╗
 * ║  GLASS / SCREEN ORDER VERIFICATION                                    ║
 * ║                                                                       ║
 * ║  Why this exists: a glass order once shipped 49 pieces for 7 windows  ║
 * ║  (a qty-squaring bug) and cost ~$3,300. This module is the permanent  ║
 * ║  guard. Before any glass order exports, the dashboard runs these      ║
 * ║  checks and shows a summary the user must confirm.                    ║
 * ║                                                                       ║
 * ║  Two independent checks:                                              ║
 * ║   1. RULE check — every window type has a KNOWN glass-piece count.    ║
 * ║      DH=2, 2HS=2, 3HS=3, Picture=1, Casement=1 (Q/H/Q=2), BSMT=1,     ║
 * ║      Brickmould=0. expected = Σ (window.qty × piecesForType).         ║
 * ║   2. PDF check — the actual rows the PDF builder produced are summed  ║
 * ║      and compared to the rule expectation. If they differ, something  ║
 * ║      in the calc or the PDF builder double-counted.                   ║
 * ╚═══════════════════════════════════════════════════════════════════════╝
 */

// ── The physical rule: glass pieces per ONE window of each type ──────────────
// Keyed by the `type` field the calc functions return.
export const GLASS_PIECES_PER_WINDOW = {
  DH:   2,   // Double Hung — Keeper + Lock
  HS:   2,   // 2-Lite Horizontal Slider — two panes
  '3HS': 3,  // 3-Lite Horizontal Slider — three panes
  PW:   1,   // Picture Window — one fixed pane
  CSMT: 1,   // Casement — one pane (Q/H/Q handled below: +1 center)
  BSMT: 1,   // Basement / Hopper — one pane
  BM:   0,   // Brickmould — trim package, no glass
};

// Human labels for the summary popup.
export const TYPE_LABELS = {
  DH: 'Double Hung', HS: '2-Lite Slider', '3HS': '3-Lite Slider',
  PW: 'Picture', CSMT: 'Casement', BSMT: 'Basement', BM: 'Brickmould',
};

// ── Expected glass pieces for a single calculated window ─────────────────────
// Uses the rule table. Q/H/Q casement is the one config that adds a pane
// (the fixed center), so it counts as 2.
export function expectedGlassForWindow(win) {
  if (!win) return 0;
  const type = win.type;
  const qty  = Math.max(1, parseInt(win.qty) || 1);
  let per = GLASS_PIECES_PER_WINDOW[type];
  if (per == null) per = null;            // unknown type — caller flags it
  if (per == null) return null;
  if (type === 'CSMT' && (win.config === 'Q/H/Q')) per = 2;
  // numLites multiplies panes for mulled units (2-Lite DH etc.)
  const liteMap = { Single:1,'2-Lite':2,'3-Lite':3,'4-Lite':4,'5-Lite':5,'Q/H/Q':2,
                    Twin:2,Triple:3,Quad:4,Quin:5 };
  const lites = liteMap[win.config] || 1;
  // CSMT Q/H/Q already accounted (per=2); don't double-apply lites for it
  const effectiveLites = (type === 'CSMT' && win.config === 'Q/H/Q') ? 1 : lites;
  return qty * per * effectiveLites;
}

// ── Actual glass pieces a calculated window's glass[] array represents ───────
// Sums every pane's qty — this is what the calc actually produced.
export function actualGlassForWindow(win) {
  if (!win || !Array.isArray(win.glass)) return 0;
  return win.glass.reduce((s, p) => s + (parseInt(p.qty) || 0), 0);
}

// ── Build a per-job summary from the database (calculatedWindows) ────────────
// Returns: { jobs:[{ woNumber, customerName, lines:[{type,label,windows,
//            expectedGlass}], expectedGlass, actualGlass, ok }],
//            totalExpected, totalActual, allOk, unknownTypes:[] }
export function buildGlassSummary(rangeJobs) {
  const jobs = [];
  const unknownTypes = new Set();
  let totalExpected = 0, totalActual = 0;

  (rangeJobs || []).forEach(job => {
    const cw = job.calculatedWindows || [];
    const byType = new Map();
    let jobExpected = 0, jobActual = 0;

    cw.forEach(win => {
      const type = win.type || '?';
      const exp  = expectedGlassForWindow(win);
      const act  = actualGlassForWindow(win);
      if (exp == null) unknownTypes.add(type);
      jobExpected += (exp || 0);
      jobActual   += act;

      if (!byType.has(type)) {
        byType.set(type, { type, label: TYPE_LABELS[type] || type, windows: 0, expectedGlass: 0 });
      }
      const rec = byType.get(type);
      rec.windows      += Math.max(1, parseInt(win.qty) || 1);
      rec.expectedGlass += (exp || 0);
    });

    totalExpected += jobExpected;
    totalActual   += jobActual;

    jobs.push({
      woNumber:      job.woNumber || '—',
      customerName:  job.customerName || 'Customer',
      lines:         [...byType.values()],
      expectedGlass: jobExpected,
      actualGlass:   jobActual,
      ok:            jobExpected === jobActual,
    });
  });

  return {
    jobs,
    totalExpected,
    totalActual,
    allOk: totalExpected === totalActual && unknownTypes.size === 0,
    unknownTypes: [...unknownTypes],
  };
}

// ── Verify the ACTUAL PDF rows against the database expectation ──────────────
// pdfGroups: the `groups` array the range PDF builder returns —
//   [{ job, tableRows:[{ _qty, ... }] }]
// Returns: { pdfTotal, dbExpected, match, perJob:[{woNumber, pdfQty, expected, ok}] }
export function verifyGlassPDF(pdfGroups, rangeJobs) {
  const dbSummary = buildGlassSummary(rangeJobs);

  let pdfTotal = 0;
  const perJob = [];
  (pdfGroups || []).forEach(g => {
    const pdfQty = (g.tableRows || []).reduce((s, r) => s + (parseInt(r._qty) || 0), 0);
    pdfTotal += pdfQty;
    const wo = g.job && g.job.woNumber;
    const dbJob = dbSummary.jobs.find(j => String(j.woNumber) === String(wo));
    perJob.push({
      woNumber: wo || '—',
      pdfQty,
      expected: dbJob ? dbJob.expectedGlass : null,
      ok: dbJob ? dbJob.expectedGlass === pdfQty : false,
    });
  });

  return {
    pdfTotal,
    dbExpected: dbSummary.totalExpected,
    match: pdfTotal === dbSummary.totalExpected,
    perJob,
  };
}
